import React, { useEffect, useState } from 'react';
import './CreateJobAdvanced.scss';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  Button,
  Alert
} from '@mui/material';
import { createJobConfigHooks } from './hooks';
import { JobDetailsCard } from './components/JobDetailsCard';
import { NotificationsCard } from './components/NotificationsCard';
import { ScheduleCard } from './components/ScheduleCard';
import { KronosAuthCard } from './components/KronosAuthCard';
import { SftpCredentialsFields } from './components/SftpCredentialsFields';
import ErrorBoundary from 'components/Error/ErrorBoundary';
import { useModalContext } from '../ModalContext';
import { diagnosticsSftpListDirectory } from '_services/ApiService';
import {
  configForDirection,
  defaultsFor,
  directionForJobType,
  isConcealedSecret,
  parseFeedConfig
} from './sftpFileFeedConfig';

const useFeedWrite = (direction) => {
  const { formState, setFormState } = useModalContext();
  return (updater, extraValues) => {
    const updated = JSON.parse(JSON.stringify(formState));
    const config = parseFeedConfig(updated.values.config, direction);
    const next = updater(config);
    updated.values.config = JSON.stringify(next, null, 2);
    if (extraValues) Object.assign(updated.values, extraValues);
    setFormState(updated);
  };
};

const ClientCard = ({ useJobConfig, useJobGeneralConfig }) => {
  const [companyName, setCompanyName] = useJobConfig(['companyName']);
  const [partner, setPartner] = useJobGeneralConfig(['partner']);
  return (
    <Card>
      <CardHeader
        subheader="Which client this feed belongs to"
        title="Client"
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="UKG company short name, used on the report and in notifications"
              label="Company short name"
              onChange={(e) => setCompanyName(e.target.value)}
              required
              value={companyName || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="Vendor or partner, for example Slavic or LRS"
              label="Partner"
              onChange={(e) => setPartner(e.target.value)}
              value={partner || ''}
              variant="standard"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const SftpConnectionCard = ({ direction, useJobConfig, useJobGeneralConfig }) => {
  const write = useFeedWrite(direction);
  const [sftp] = useJobConfig(['sftp']);
  const [directory] = useJobConfig(['directory']);
  const [, setVerified] = useJobGeneralConfig(['sftpConnectionVerified']);
  const connection = sftp || {};
  const [testStatus, setTestStatus] = useState('');

  const writeSftp = (nextSftp, nextDirectory) => {
    write(
      (config) => ({
        ...config,
        sftp:
          typeof nextSftp === 'function' ? nextSftp(config.sftp || {}) : nextSftp,
        directory:
          nextDirectory !== undefined ? nextDirectory : config.directory || ''
      }),
      { sftpConnectionVerified: false }
    );
    setTestStatus('');
  };

  const testConnection = async () => {
    setTestStatus('testing');
    try {
      const result = await diagnosticsSftpListDirectory({
        credentials: connection,
        directory: directory || '/'
      });
      if (result?.type === 'error') {
        throw new Error(result.message || 'SFTP connection failed');
      }
      setVerified(true);
      setTestStatus('ok');
    } catch (error) {
      setVerified(false);
      setTestStatus(error.message || 'SFTP connection failed');
    }
  };

  return (
    <Card>
      <CardHeader
        subheader="Where the files live. Test the connection before activating the feed."
        title="SFTP connection"
      />
      <CardContent>
        <SftpCredentialsFields connection={connection} onChange={writeSftp} />

        <TextField
          fullWidth
          helperText={
            direction === 'import'
              ? 'Folder to read. Every file in this folder is imported.'
              : 'Folder where the export file is written.'
          }
          label="File directory"
          onChange={(e) => writeSftp((current) => current, e.target.value)}
          required
          sx={{ marginTop: 2 }}
          value={directory || ''}
          variant="standard"
        />

        <Button
          color="primary"
          onClick={testConnection}
          sx={{ marginTop: 2 }}
          variant="contained"
        >
          Test connection
        </Button>
        {testStatus === 'ok' && (
          <Alert severity="success" sx={{ marginTop: 2 }}>
            SFTP connection succeeded. You can activate the feed.
          </Alert>
        )}
        {testStatus && testStatus !== 'ok' && testStatus !== 'testing' && (
          <Alert severity="error" sx={{ marginTop: 2 }}>
            {testStatus}
          </Alert>
        )}
        {testStatus === 'testing' && (
          <Alert severity="info" sx={{ marginTop: 2 }}>
            Connecting…
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

function storedSftpCredentials(sftp = {}) {
  if (sftp.type === '1password_item') {
    return !!sftp['1password_item_path'];
  }
  return isConcealedSecret(sftp.password) || isConcealedSecret(sftp.privateKey);
}

const ImportDetailsCard = ({ useJobConfig }) => {
  const [importDetails, setImportDetails] = useJobConfig(['importDetails']);
  const details = importDetails || { type_id: '' };
  return (
    <Card>
      <CardHeader
        subheader="Which UKG import receives the file"
        title="Import"
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="UKG import type, for example 124 for scheduled deductions"
              label="Import type ID"
              onChange={(e) =>
                setImportDetails({ ...details, type_id: e.target.value })
              }
              required
              value={details.type_id || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="Numeric UKG company ID the file is imported into"
              label="UKG company ID"
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  const next = { ...details };
                  delete next.company_id;
                  setImportDetails(next);
                  return;
                }
                const asNumber = Number(raw);
                if (!Number.isFinite(asNumber)) return;
                setImportDetails({ ...details, company_id: asNumber });
              }}
              required
              type="number"
              value={details.company_id ?? ''}
              variant="standard"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const ACTION_OPTIONS = [
  { value: 'doNothing', label: 'Leave the file in place' },
  { value: 'delete', label: 'Delete the file' },
  { value: 'moveTo', label: 'Move the file to another folder' }
];

const SftpEventFields = ({ label, event, onChange }) => {
  const action = event?.action || 'doNothing';
  return (
    <Grid container spacing={2}>
      <Grid item md={6} xs={12}>
        <TextField
          fullWidth
          label={label}
          onChange={(e) => {
            const next = e.target.value;
            onChange(
              next === 'moveTo'
                ? { action: next, directory: event?.directory || '' }
                : { action: next }
            );
          }}
          select
          // eslint-disable-next-line react/jsx-sort-props
          SelectProps={{ native: true }}
          value={action}
          variant="standard"
        >
          {ACTION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </TextField>
      </Grid>
      {action === 'moveTo' && (
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            label="Destination folder"
            onChange={(e) => onChange({ action: 'moveTo', directory: e.target.value })}
            required
            value={event?.directory || ''}
            variant="standard"
          />
        </Grid>
      )}
    </Grid>
  );
};

const AfterTransferCard = ({ useJobConfig }) => {
  const [onComplete, setOnComplete] = useJobConfig(['onComplete']);
  const [onFailure, setOnFailure] = useJobConfig(['onFailure']);
  return (
    <Card>
      <CardHeader
        subheader="What to do with the vendor file after Wurk accepts or rejects it"
        title="After import"
      />
      <CardContent>
        <SftpEventFields
          event={onComplete}
          label="After a successful import"
          onChange={setOnComplete}
        />
        <div style={{ height: 16 }} />
        <SftpEventFields
          event={onFailure}
          label="After a failed import"
          onChange={setOnFailure}
        />
      </CardContent>
    </Card>
  );
};

const ExportFileCard = ({ useJobConfig }) => {
  const [reportID, setReportID] = useJobConfig(['reportID']);
  const [isExportReport, setIsExportReport] = useJobConfig(['isExportReport']);
  const [fileNamingSchema, setFileNamingSchema] = useJobConfig(['fileNamingSchema']);
  const [pgp, setPgp] = useJobConfig(['encriptPGPConfig']);
  const encryptionOn = !!pgp?.isEnabled;

  return (
    <Card>
      <CardHeader
        subheader="Which report to send, and what to call the file"
        title="Export file"
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="UKG report or export ID"
              label="Report ID"
              onChange={(e) => setReportID(e.target.value)}
              required
              value={reportID || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!isExportReport}
                  onChange={(e) => setIsExportReport(e.target.checked)}
                />
              }
              label={
                <>
                  Export report
                  <FormHelperText>
                    Turn this on when the report ID is a UKG export that has to be downloaded. Leave it off for a saved report.
                  </FormHelperText>
                </>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              helperText="Include {DIRECTORY} so the file lands in the folder above. Tokens: {DIRECTORY} {REPORT_ID} {YEAR_4_DIGITS} {MONTH_LEADING_ZERO} {MONTH_OFFSET:-1} {DAY_LEADING_ZERO} {HOUR_24} {MINUTE} {SECOND}"
              label="File name pattern"
              onChange={(e) => setFileNamingSchema(e.target.value)}
              required
              value={fileNamingSchema ?? ''}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={encryptionOn}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPgp({
                        isEnabled: true,
                        publicKey: pgp?.publicKey || ''
                      });
                    } else {
                      setPgp(undefined);
                    }
                  }}
                />
              }
              label="Encrypt the file with a PGP public key"
            />
          </Grid>
          {encryptionOn && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="PGP public key"
                multiline
                onChange={(e) =>
                  setPgp({ isEnabled: true, publicKey: e.target.value })
                }
                required
                value={
                  typeof pgp?.publicKey === 'string' ? pgp.publicKey : ''
                }
                variant="standard"
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

const ExportAdvancedCard = ({ useJobConfig }) => {
  const [loginAsUsername, setLoginAsUsername] = useJobConfig(['loginAsUsername']);
  return (
    <Card>
      <CardHeader subheader="Rarely needed" title="Advanced" />
      <CardContent>
        <TextField
          fullWidth
          helperText="Only needed if this client requires Kronos impersonation of a specific user"
          label="Login as username"
          onChange={(e) => setLoginAsUsername(e.target.value || undefined)}
          value={loginAsUsername || ''}
          variant="standard"
        />
      </CardContent>
    </Card>
  );
};

export const SftpFileFeedForm = () => {
  const { formState, setFormState } = useModalContext();
  const direction = directionForJobType(formState.values.jobType);
  const { useJobGeneralConfig, useJobConfig } = createJobConfigHooks({
    partner: '',
    defaultConfig: defaultsFor(direction)
  });

  useEffect(() => {
    const raw = formState.values.config;
    const isBlank = !raw || raw === '{ }' || raw === '{}';
    const updated = JSON.parse(JSON.stringify(formState));
    let changed = false;

    if (!isBlank) {
      try {
        const config = JSON.parse(raw);
        const wantsExport = direction === 'export';
        const looksLikeImport = !!(config.importDetails || config.onComplete);
        const looksLikeExport =
          config.reportID !== undefined || config.isExportReport !== undefined;
        const mismatch =
          (wantsExport && looksLikeImport && !looksLikeExport) ||
          (!wantsExport && looksLikeExport && !looksLikeImport);
        if (mismatch) {
          updated.values.config = JSON.stringify(
            configForDirection(direction, config),
            null,
            2
          );
          updated.values.sftpConnectionVerified = false;
          changed = true;
        } else if (
          formState.values.status === 'enabled' &&
          !formState.values.sftpConnectionVerified &&
          storedSftpCredentials(config.sftp)
        ) {
          // An enabled feed was already saved. Editing it does not require
          // another connection test until the SFTP fields change.
          updated.values.sftpConnectionVerified = true;
          changed = true;
        }
      } catch (error) {
        // Invalid JSON is reported when the user saves.
      }
    }

    if (changed) setFormState(updated);
    // Only react when the selected feed type changes. Rewriting here on every
    // config edit would clobber the field the user just changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.values.jobType]);

  return (
    <ErrorBoundary context="SFTP file feed form">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <JobDetailsCard useJobGeneralConfig={useJobGeneralConfig} />
        </Grid>
        <Grid item xs={12}>
          <ClientCard
            useJobConfig={useJobConfig}
            useJobGeneralConfig={useJobGeneralConfig}
          />
        </Grid>
        <Grid item xs={12}>
          <KronosAuthCard useJobConfig={useJobConfig} />
        </Grid>
        <Grid item xs={12}>
          <SftpConnectionCard
            direction={direction}
            useJobConfig={useJobConfig}
            useJobGeneralConfig={useJobGeneralConfig}
          />
        </Grid>
        {direction === 'import' ? (
          <>
            <Grid item xs={12}>
              <ImportDetailsCard useJobConfig={useJobConfig} />
            </Grid>
            <Grid item xs={12}>
              <AfterTransferCard useJobConfig={useJobConfig} />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12}>
              <ExportFileCard useJobConfig={useJobConfig} />
            </Grid>
            <Grid item xs={12}>
              <ExportAdvancedCard useJobConfig={useJobConfig} />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <NotificationsCard useJobConfig={useJobConfig} />
        </Grid>
        <Grid item xs={12}>
          <ScheduleCard useJobGeneralConfig={useJobGeneralConfig} />
        </Grid>
      </Grid>
    </ErrorBoundary>
  );
};

export default SftpFileFeedForm;
